/**
 * LoadingHeader Component
 * A clean, modern loading header that appears while the app is initializing
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

export const LoadingHeader: React.FC = () => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border sticky top-0 z-50"
    >
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo/Brand */}
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">C</span>
          </div>
          <span className="font-semibold text-lg">Chatslytics</span>
        </div>

        {/* Loading indicator */}
        <div className="flex items-center space-x-3 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Initializing...</span>
        </div>
      </div>
    </motion.div>
  );
};

export const LoadingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <LoadingHeader />
      <div className="container mx-auto px-4 py-16 flex items-center justify-center">
        <div className="text-center space-y-6">
          <div className="w-16 h-16 mx-auto">
            <Loader2 className="w-full h-full animate-spin text-primary" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold">Loading Chatslytics</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Please wait while we set up your workspace and verify your authentication...
            </p>
          </div>
          
          {/* Progress dots */}
          <div className="flex items-center justify-center space-x-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-2 h-2 bg-primary rounded-full"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.2,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
